import { useMemo, useState } from 'react';
import { useT } from '../i18n';
import { clean, renderPlainText, type Block } from '../mail/plaintext';

/*
    A plain-text body, rendered like mail.

    Quotes get the same left rule the HTML reader draws, nested one step per
    level; the signature is dimmed; the chain underneath the reply folds behind
    the same `···` control every mail client has taught people to look for.
*/

function Line({ block }: { block: Block }) {
    if (block.kind === 'signature' && !block.html) {
        return <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '14px 0 10px', maxWidth: 180, marginInlineStart: 0 }} />;
    }

    const quoted = block.kind === 'quote';
    const style: React.CSSProperties = {
        margin: quoted ? '2px 0' : '0 0 10px',
        // One rule per level of quoting, so depth is visible rather than counted.
        paddingInlineStart: quoted ? 12 : 0,
        marginInlineStart: quoted ? (block.depth - 1) * 10 : 0,
        borderInlineStart: quoted ? '2px solid var(--border2)' : undefined,
        color: block.kind === 'text' ? 'var(--text)' : 'var(--text-mute)',
        fontFamily: 'var(--font-sans)',
        fontSize: block.kind === 'attribution' ? 12.5 : 13.5,
        lineHeight: 1.65,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
    };

    return <div dir="auto" style={style} dangerouslySetInnerHTML={{ __html: clean(block.html) }} />;
}

export default function PlainBody({ text, markdown = true }: { text: string; markdown?: boolean }) {
    const { t } = useT();
    const [open, setOpen] = useState(false);
    const { lead, trailing } = useMemo(() => renderPlainText(text, { markdown }), [text, markdown]);

    return (
        <div className="plain-body">
            {lead.map((block, i) => (
                <Line key={i} block={block} />
            ))}

            {trailing.length ? (
                <>
                    <button
                        data-testid="quote-toggle"
                        onClick={() => setOpen((v) => !v)}
                        title={t(open ? 'reader.hideQuoted' : 'reader.showQuoted')}
                        aria-label={t(open ? 'reader.hideQuoted' : 'reader.showQuoted')}
                        aria-expanded={open}
                        style={{
                            display: 'block',
                            margin: '6px 0 10px',
                            padding: '1px 9px 4px',
                            lineHeight: 1,
                            fontSize: 'var(--fs-lg)',
                            letterSpacing: 1,
                            background: 'var(--chip-bg)',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--r-pill)',
                            color: 'var(--text-mute)',
                        }}
                    >
                        ···
                    </button>
                    {open ? trailing.map((block, i) => <Line key={i} block={block} />) : null}
                </>
            ) : null}
        </div>
    );
}
